import { fabric } from 'fabric';
import FontFaceObserver from 'fontfaceobserver';
import { DesignLayer, DesignLayerType, Design, Dimension } from '@/types';
import { CanvasManager } from './CanvasManager';
import randomcolor from 'randomcolor';

export class FotowandCanvasManager extends CanvasManager {
  public backgroundImage: fabric.Image;
  public textObject: fabric.Text;
  public imageObject: fabric.Image;
  private overlay = new fabric.Rect({
    fill: '#fff',
    opacity: 0.85,
    top: -1,
    left: 0,
    evented: false,
    selectable: false,
  });
  private mask = new fabric.Rect({
    fill: 'transparent',
    left: 0,
    top: 0,
    inverted: true,
    originX: 'center',
    originY: 'center',
  });
  private designRect = new fabric.Rect({
    top: 0,
    left: 0,
    width: 100,
    height: 100,
    fill: 'transparent',
    stroke: '#0a0a0a',
    evented: false,
    selectable: false,
    originX: 'center',
    originY: 'center',
  });
  private wallTexture: fabric.Rect;
  private backgroundTexture: boolean;
  private designRectStrokeWidth: number;
  private topDimension = new fabric.Text('0 cm', {
    fontFamily: 'ManropeGX, Manrope, sans-serif',
    fontSize: 12,
    fontWeight: 500,
    fill: '#495D70',
    evented: false,
    selectable: false,
    originX: 'center',
    originY: 'center',
  });
  private rightDimension = new fabric.Text('0 cm', {
    fontFamily: 'ManropeGX, Manrope, sans-serif',
    fontWeight: 500,
    fontSize: 12,
    fill: '#495D70',
    evented: false,
    selectable: false,
    originX: 'center',
    originY: 'center',
  });
  public async setType(type) {
    if (type === 'CANVAS') {
      this.designRectStrokeWidth = 10;
      this.backgroundTexture = false;
      return;
    }

    this.designRectStrokeWidth = 0;
    if (!this.wallTexture) {
      this.wallTexture = await this.createPattern(require('@/assets/texture/wall.jpg'));

      this.wallTexture.set({
        opacity: 0.1,
      });
    }

    this.backgroundTexture = true;
    this.relocateObjects();
  }
  private createImage(src) {
    return new Promise<fabric.Image>((res) => {
      fabric.util.loadImage(src, (img) => {
        const object = new fabric.Image(img);
        object.set({
          evented: false,
          selectable: false,
          originX: 'center',
          originY: 'center',
        });
        res(object);
      });
    });
  }
  private createPattern(src) {
    return new Promise<fabric.Rect>((res) => {
      fabric.util.loadImage(src, (img) => {
        const object = new fabric.Rect({
          evented: false,
          selectable: false,
          originX: 'center',
          originY: 'center',

          fill: new fabric.Pattern({
            source: img,
            repeat: 'repeat',
          }),
        });
        res(object);
      });
    });
  }
  removeObject(object) {
    if (!object) {
      return;
    }
    const { type } = object.data;

    if (type === 'Background') {
      this.backgroundImage = null;
    } else if (type === 'Image') {
      this.imageObject = null;
    }

    super.removeObject(object);
  }
  /**
   * dimensions in CM
   */
  private physicalSize = {
    width: 1,
    height: 1,
  };
  public get selectedObject() {
    if (!this.canvas) {
      return null;
    }
    return this.canvas.getActiveObject();
  }
  async initialize(element: HTMLCanvasElement) {
    super.initialize(element);
    // Setting scene up
    this.overlay.clipPath = this.mask;
    this.canvas.add(this.overlay);
    this.canvas.add(this.designRect);
    await this.setType('BEHANG');

    this.resize();

    window.addEventListener('background:selected', this.previewMode.bind(this));
    window.addEventListener('object:selected', this.previewMode.bind(this));
    window.addEventListener('selection:cleared', this.previewMode.bind(this));

    const observer = new FontFaceObserver('ManropeGX', { weight: 500 });
    try {
      await observer.load();
    } catch (e) {
      console.error('Unable to load ManropeGX font in canvas');
    }

    this.canvas.add(this.topDimension);
    this.canvas.add(this.rightDimension);
  }
  protected previewMode(e) {
    if (e && e.detail) {
      this.overlay.animate('opacity', '0.85', {
        duration: 400,
        onChange: this.canvas.renderAll.bind(this.canvas),
      });
      this.canvas.requestRenderAll();
      return;
    }

    this.overlay.animate('opacity', '1', {
      duration: 400,
      onChange: this.canvas.renderAll.bind(this.canvas),
    });
    this.canvas.requestRenderAll();
  }
  protected OnObjectSelected(object: fabric.Object) {
    if (object == this.backgroundImage) {
      super.OnBackgroundSelected(object);
      this.OnObjectUnSelected(object);
    } else {
      super.OnObjectSelected(object);
    }
  }
  protected resize() {
    super.resize();
    this.overlay.width = this.width;
    this.overlay.height = this.height + 2;
    this.resizeDesignRect(this.physicalSize.width, this.physicalSize.height);
    this.canvas.requestRenderAll();
  }
  /**
   *
   * @param width CM
   * @param height CM
   */
  private calculateMaskSize(width: number, height: number) {
    const padding = 0.8; // %
    this.physicalSize.width = width;
    this.physicalSize.height = height;
    const ratio = height / width;
    if (isNaN(ratio)) return;

    // const maxWidth = 800;

    width = this.width * padding;
    height = this.width * ratio * padding;

    // if (width > maxWidth) {
    //     width = maxWidth;
    //     height = maxWidth * ratio;
    // }

    if (height > this.height * padding) {
      width = (this.height * padding) / ratio;
      height = this.height * padding;
    }

    return { width, height };
  }
  /**
   *
   * @param width CM
   * @param height CM
   */
  public async resizeDesignRect(width: number, height: number) {
    const changed = width != this.physicalSize.width || height != this.physicalSize.height;
    const size = this.calculateMaskSize(width, height);
    this.mask.set(size);

    const center = this.canvas.getCenter();
    this.designRect.set({
      ...center,
      ...size,
      strokeWidth: this.designRectStrokeWidth,
    });

    this.canvas.remove(this.wallTexture);
    if (this.backgroundTexture) {
      this.wallTexture.set({
        ...center,
        ...size,
      });

      this.canvas.add(this.wallTexture);
    }

    this.mask.dirty = true;
    this.overlay.dirty = true;

    // this.relocateObjects();
    this.relocateBackground(changed);

    const border = this.designRect.strokeWidth;
    let top = center.top - size.height / 2 - this.topDimension.height / 2 - border - 5;
    if (this.backgroundTexture) {
      top -= 10;
    }
    this.topDimension.set({
      text: `${width} cm`,
      top,
      left: center.left,
    });

    let left = center.left + size.width / 2 + this.rightDimension.width / 2 + border + 10;
    if (this.backgroundTexture) {
      left += 10;
    }
    this.rightDimension.set({
      angle: 0,
      text: `${height} cm`,
      left,
      top: center.top,
    });

    this.reorderWorkbench();

    const canvasWidth: number = this.canvas.getWidth();
    const rightDimensionWidth = this.rightDimension.get('width');
    const leftOverSpace = (canvasWidth - size.width) / 2;

    if (rightDimensionWidth + 30 > leftOverSpace) {
      this.rightDimension.rotate(-90);
      this.rightDimension.set({
        angle: 90,
        left: left - 20,
        top: center.top,
      });
    }
  }
  public addRendomCircle() {
    const radius = Math.random() * 50 + 50;
    this.addObject(
      new fabric.Circle({
        originX: 'center',
        originY: 'center',
        fill: randomcolor(),
        radius,
        top: Math.random() * this.height * 0.5 + this.height * 0.2,
        left: Math.random() * this.width * 0.5 + this.width * 0.2,
      }),
    );
  }
  public async setBackground(src: string | File, id?: string, original?: object) {
    if (this.backgroundImage) {
      this.canvas.remove(this.backgroundImage);
      this.backgroundImage.dispose();
      delete this.backgroundImage;
    }
    this.backgroundImage = await this.loadImage(src);
    this.backgroundImage.set({
      originX: 'center',
      originY: 'center',
      hasRotatingPoint: false,
      lockScalingX: true,
      lockScalingY: true,
      hasControls: false,
      hasBorders: false,
      id,
    } as Record<string, any>); // eslint-disable-line @typescript-eslint/no-explicit-any

    this.backgroundImage.on('moving', this.onBackgroundMoving.bind(this));
    const object = this.addObject(this.backgroundImage, DesignLayerType.Background) as fabric.Image;
    this.relocateBackground(true);

    //set original on init
    object.data.original = original;

    this.selectBackground(object);
    this.objectLoaded();

    return object;
  }
  private onBackgroundMoving() {
    const border = this.designRect.strokeWidth / 2;
    const backgroundImageRect = this.backgroundImage.getBoundingRect();
    if (this.backgroundImage.lockMovementX) {
      const movingSpace = (backgroundImageRect.height - this.designRect.height) / 2 - border;
      if (this.backgroundImage.top > this.designRect.top + movingSpace) {
        this.backgroundImage.top = this.designRect.top + movingSpace;
      } else if (this.backgroundImage.top < this.designRect.top - movingSpace) {
        this.backgroundImage.top = this.designRect.top - movingSpace;
      }
    } else {
      const movingSpace = (backgroundImageRect.width - this.designRect.width) / 2 - border;
      if (this.backgroundImage.left > this.designRect.left + movingSpace) {
        this.backgroundImage.left = this.designRect.left + movingSpace;
      } else if (this.backgroundImage.left < this.designRect.left - movingSpace) {
        this.backgroundImage.left = this.designRect.left - movingSpace;
      }
    }
  }
  private get designBBox() {
    const bbox = this.designRect.getBoundingRect(false, true);
    // bbox.left += this.designRect.strokeWidth;
    // bbox.top += this.designRect.strokeWidth;
    // bbox.width -= this.designRect.strokeWidth;
    // bbox.height -= this.designRect.strokeWidth;
    return bbox;
  }
  /**
   * Relocates object based on relative `object.data.position` and `object.data.size` within `object.data.*`
   * @param object
   */
  private relocateObject(object: fabric.Object) {
    const designRectBounds = this.designBBox;
    object.set({
      left: object.data.position.x * designRectBounds.width + designRectBounds.left,
      top: object.data.position.y * designRectBounds.height + designRectBounds.top,
      // scaleX: (object.data.dimensions.width * designRectBounds.width) / object.width,
      // scaleY: (object.data.dimensions.height * designRectBounds.height) / object.height,
    });

    object.scaleToWidth(object.data.dimensions.width * designRectBounds.width);
    object.setCoords();
  }
  private relocateObjects() {
    this.canvas.getObjects().forEach((object) => {
      switch (object.data?.type) {
        case DesignLayerType.Image:
        case DesignLayerType.Text:
          this.relocateObject(object);
          break;
      }
    });
  }
  public relocateBackground(dimensionsChanged = false) {
    if (this.backgroundImage) {
      const border = this.designRect.strokeWidth * 1;
      const designRectBounds = this.designBBox;
      const designRectRatio = designRectBounds.width / designRectBounds.height;
      const rotated = this.backgroundImage.angle % 180 != 0;
      const backgroundSize = {
        width: rotated ? this.backgroundImage.height : this.backgroundImage.width,
        height: rotated ? this.backgroundImage.width : this.backgroundImage.height,
      };

      const backgroundImageRatio = backgroundSize.width / backgroundSize.height;
      // Calculating Scale
      if ((designRectRatio > 1 && backgroundImageRatio < 1) || designRectRatio > backgroundImageRatio) {
        this.backgroundImage.scaleX = (designRectBounds.width - border) / backgroundSize.width;
        this.backgroundImage.scaleY = this.backgroundImage.scaleX;
      } else if (designRectRatio == backgroundImageRatio) {
        this.backgroundImage.scaleX = (designRectBounds.width - border) / backgroundSize.width;
        this.backgroundImage.scaleY = this.backgroundImage.scaleX;
      } else {
        this.backgroundImage.scaleY = (designRectBounds.height - border) / backgroundSize.height;
        this.backgroundImage.scaleX = this.backgroundImage.scaleY;
      }

      // Calculating MoveX & MoveY
      this.backgroundImage.lockMovementX = designRectRatio >= backgroundImageRatio;
      this.backgroundImage.lockMovementY = designRectRatio <= backgroundImageRatio;

      if (dimensionsChanged) {
        this.backgroundImage.set({
          ...this.canvas.getCenter(),
        });
      } else {
        this.backgroundImage.top =
          this.backgroundImage.data.position.y * designRectBounds.height + designRectBounds.top;
        this.backgroundImage.left =
          this.backgroundImage.data.position.x * designRectBounds.width + designRectBounds.left;
      }
      this.backgroundImage.setCoords();
      this.updateRelativePosition(this.backgroundImage);
      this.updateRelativeDimension(this.backgroundImage);
    }
  }

  private reorderWorkbench() {
    this.overlay.bringToFront();
    this.designRect.bringToFront();
    if (this.backgroundImage) {
      this.backgroundImage.sendToBack();
    }
    if (this.backgroundTexture) {
      this.wallTexture.sendToBack();
    }

    this.topDimension.bringToFront();
    this.rightDimension.bringToFront();
    this.canvas.requestRenderAll();
  }
  private updateRelativeDimension(object: fabric.Object) {
    object.data.dimensions = {
      width: object.getScaledWidth() / this.designRect.width,
      height: object.getScaledHeight() / this.designRect.height,
    } as Dimension;
  }
  private updateRelativePosition(object: fabric.Object) {
    const designRectBounds = this.designBBox;
    object.data.position = {
      x: (object.left - designRectBounds.left) / designRectBounds.width,
      y: (object.top - designRectBounds.top) / designRectBounds.height,
    };
  }
  private addObject(object: fabric.Object, type?: DesignLayerType, id?: string) {
    object.data = Object.assign({}, object.data, { type });

    const location = this.canvas.getCenter();
    object.set({
      ...location,
      originX: 'center',
      originY: 'center',
      hasRotatingPoint: false,
      lockUniScaling: true,
      lockRotation: true,
      padding: 4,
      cornerSize: 22,
      borderColor: '#495D70',
      borderScaleFactor: 3,
      borderOpacityWhenMoving: 1,
      borderDashArray: [15, 8],
      id,
    } as Record<string, any>); // eslint-disable-line @typescript-eslint/no-explicit-any

    this.canvas.add(object);
    this.updateRelativeDimension(object);
    this.updateRelativePosition(object);

    // Watching moving & scaling
    object.on('scaled', () => this.updateRelativeDimension(object));
    object.on('moved', () => this.updateRelativePosition(object));
    this.reorderWorkbench();

    return object;
  }
  public objectLoaded() {
    const event = new CustomEvent('object:loaded');
    window.dispatchEvent(event);
  }
  public addText(attributes: fabric.TextOptions) {
    const text = new fabric.Text(attributes.text);
    this.setText(text, attributes);

    this.textObject = this.addObject(text, DesignLayerType.Text) as fabric.Text;
    return this.textObject;
  }
  public setText(object: fabric.Text, attributes: fabric.TextOptions) {
    object.set(attributes);
    this.canvas.requestRenderAll();
  }
  public async addImage(src: string | File, id?: string, object?: fabric.Image) {
    const image = await this.loadImage(src, object);

    if (object) {
      this.selectObject(object);
      return object;
    }

    this.imageObject = this.addObject(image, DesignLayerType.Image, id) as fabric.Image;

    this.selectObject(this.imageObject);

    this.objectLoaded();

    return this.imageObject;
  }
  private get designLayers() {
    const designRectBounds = this.designBBox;
    return this.canvas
      .getObjects()
      .filter((item) => item.data?.type)
      .map((item, index) => {
        this.updateRelativeDimension(item);
        this.updateRelativePosition(item);
        item.data.position.z = index;
        const attributes = item.toObject();
        attributes.left -= designRectBounds.left + this.designRectStrokeWidth / 2;
        attributes.top -= designRectBounds.top + this.designRectStrokeWidth / 2;
        if (item.data.original) {
          attributes.placeholder = { src: attributes.src, original: item.data.original };
        }
        delete attributes.src;
        const dimensions = {
          width: item.data.dimensions.width * (1 - this.designRectStrokeWidth / designRectBounds.width),
          height: item.data.dimensions.height * (1 - this.designRectStrokeWidth / designRectBounds.height),
        };
        // debugger;
        return {
          id: item.data.id || null,
          type: item.data.type,
          position: item.data.position,
          // dimensions: item.data.dimensions,
          dimensions,
          attributes,
          image: item.data.original?.src || item.data.file,
        };
      }) as DesignLayer[];
  }
  public toDesign(): Design {
    return {
      digitalSize: {
        width: this.designRect.width,
        height: this.designRect.height,
      },
      dimensions: this.physicalSize,
      layers: this.designLayers,
    } as Design;
  }
  public bringForward(object: fabric.Object) {
    object.bringForward();
    this.reorderWorkbench();
  }
  public sendToBack(object: fabric.Object) {
    if (object) {
      object.sendToBack();
    }
    this.reorderWorkbench();
  }
  public preview(): string {
    return this.canvas.toDataURL({
      ...this.designBBox,
      format: 'png',
      multiplier: 1,
    });
  }
  public async load(design: Design) {
    this.resizeDesignRect(design.dimensions.width, design.dimensions.height);
    await Promise.all(
      design.layers.map(async (layer) => {
        let object: fabric.Object;
        switch (layer.type) {
          case DesignLayerType.Background:
            if (layer.attributes.placeholder?.src) {
              object = await this.setBackground(layer.attributes.placeholder.src);
              object.data.original = layer.attributes.placeholder?.original;
            } else {
              object = await this.setBackground(layer.image);
            }
            break;
          case DesignLayerType.Image:
            object = await this.addImage(layer.image);
            break;
          case DesignLayerType.Text: {
            object = this.addText({ ...layer.attributes, fontFamily: 'initial' });
            const font = new FontFaceObserver(layer.attributes.fontFamily);
            font.load().then(() => {
              this.setText(object as fabric.Text, { fontFamily: layer.attributes.fontFamily });
            });
            break;
          }
        }
        object.data.id = layer.id;
        object.data.position = layer.position;
        object.data.dimensions = layer.dimensions;
        this.relocateObject(object);
        object.moveTo(layer.position.z);
      }),
    );
    this.reorderWorkbench();
  }
}
